import React, { useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { clamp } from "@perceptron/utils/math";
import { formatString } from "@perceptron/utils/string";
import { animated, to, useSpring } from "react-spring";
import useResizeObserver from "use-resize-observer";

import type { TimelinePlayerBridge } from "./bridge";
import { RulerCanvas } from "./components/canvas/RulerCanvas";
import { ScrubBarCanvas } from "./components/canvas/ScrubBarCanvas";
import { TracksCanvas, TracksCanvasHandle } from "./components/canvas/TracksCanvas";
import { VideoTrackLayerHandle } from "./components/canvas/VideoTrackLayer";
import { HoverIndicator, HoverIndicatorHandle } from "./components/HoverIndicator";
import { Playhead, PlayheadHandle } from "./components/Playhead";
import { SummaryOverlaySvg } from "./components/SummaryOverlaySvg";
import { TimelineOverview } from "./components/TimelineOverview";
import { ANIM_CONFIG, SNAP_THRESHOLD_PX, RULER_HEIGHT, ROW_HEIGHT_TRACK } from "./constants";
import { useTimelineState } from "./hooks/useTimelineState";
import { SEEK_STEP_SECONDS } from "./hooks/usePlayerShortcuts";
import { useTimelineFocus } from "./hooks/useTimelineFocus";
import { TimeDisplayContainer } from "./MediaControls";
import { useTimelineDataStore, useTimelineStore } from "./store/timeline-context";
import { TrackLabelPane } from "./TrackLabelPane";
import { ZVideo } from "./types";
import { formatTimelineTime, showsHours } from "./utils/ruler";
import { segmentBoundaryTimes } from "./utils/segment-navigation";
import type { TimelineZoom } from "./utils/viewport";

/** The scrub-bar row: the video track sits in it, with the playhead's pentagon on top. */
const SCRUB_BAR_ROW_HEIGHT = ROW_HEIGHT_TRACK - 8;

export const TimelineEditor = React.memo<{
  bridge: TimelinePlayerBridge;
  /** Receives the editor's zoom command, so shortcuts can reach it through the bridge. */
  zoomRef?: React.RefObject<((zoom: TimelineZoom) => void) | null>;
}>(({ bridge, zoomRef }) => {
  const { seek, scrubSeek, subscribeToTime } = bridge;

  const containerRef = useRef<HTMLDivElement>(null);
  const setIsScrubbing = useTimelineStore((state) => state.setIsScrubbing);
  const isMinimized = useTimelineStore((state) => state.isMinimized);
  const setHighlightedSegmentIds = useTimelineStore((state) => state.setHighlightedSegmentIds);
  const focus = useTimelineFocus();
  const trackingTimeline = useTimelineStore((state) => state.trackingTimeline);

  const items = useTimelineDataStore((state) => state.items);
  const durationSeconds = useTimelineDataStore((state) => state.durationSeconds);

  const videoTrack = ZVideo.safeParse(items[0]).data;
  const scrollableItems = useMemo(() => items.slice(1), [items]);

  const timelinePaneRef = useRef<HTMLDivElement>(null);
  const rulerContainerRef = useRef<HTMLDivElement>(null);
  const videoTrackContainerRef = useRef<HTMLDivElement>(null);

  // Memoize refs array to prevent useTimelineWheel from re-attaching listeners
  const timelineRefs = useMemo(() => [timelinePaneRef, rulerContainerRef, videoTrackContainerRef], []);

  const state = useTimelineState({
    items: scrollableItems,
    durationSeconds,
    timelineRefs,
  });

  const { ref: resizeRef, width = 1, height = 1 } = useResizeObserver<HTMLDivElement>();
  useEffect(() => {
    state.setViewport(width, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, state.setViewport]);

  const labelPaneRef = useRef<HTMLDivElement>(null);
  const tracksCanvasRef = useRef<TracksCanvasHandle>(null);

  const handleLabelScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    tracksCanvasRef.current?.setScrollTop(top);
    state.setScrollTop(top);
  };

  useEffect(() => {
    if (labelPaneRef.current && labelPaneRef.current.scrollTop !== state.scrollTop) {
      labelPaneRef.current.scrollTop = state.scrollTop;
    }
    tracksCanvasRef.current?.setScrollTop(state.scrollTop);
  }, [state.scrollTop]);

  const combinedTimelineRef = useCallback(
    (node: HTMLDivElement) => {
      (timelinePaneRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (resizeRef) {
        (resizeRef as React.RefCallback<HTMLDivElement>)(node);
      }
    },
    [resizeRef],
  );

  const playheadRef = useRef<PlayheadHandle>(null);
  const hoverRef = useRef<HoverIndicatorHandle>(null);
  const videoTrackLayerRef = useRef<VideoTrackLayerHandle>(null);

  // Keep the Konva scrub-bar progress outside React's playback render path.
  // Playhead owns its own subscription because it also reacts to layout changes.
  useEffect(() => {
    let lastTime = -1;
    return subscribeToTime((time) => {
      if (time === lastTime) return;
      lastTime = time;
      videoTrackLayerRef.current?.updateProgress(time);
    });
  }, [subscribeToTime]);

  // The ruler is the timeline's slider. Its value follows playback imperatively,
  // in whole seconds, so screen readers aren't flooded.
  useEffect(() => {
    let lastSecond = -1;
    const showHours = showsHours(durationSeconds);
    const total = formatTimelineTime(durationSeconds, 0, showHours);
    return subscribeToTime((time) => {
      const second = Math.floor(time);
      const ruler = rulerContainerRef.current;
      if (second === lastSecond || !ruler) return;
      lastSecond = second;
      ruler.setAttribute("aria-valuenow", String(second));
      ruler.setAttribute(
        "aria-valuetext",
        formatString(strings.videoPlayer.timelinePositionValue, formatTimelineTime(second, 0, showHours), total),
      );
    });
  }, [subscribeToTime, durationSeconds]);

  // Exclude the video scrub-bar track so the playhead doesn't snap to 0 / duration — those boundaries are already clamped.
  const snapPoints = useMemo(
    () => segmentBoundaryTimes(scrollableItems, trackingTimeline, durationSeconds),
    [scrollableItems, trackingTimeline, durationSeconds],
  );
  const scrubSnapPointsRef = useRef<readonly number[] | null>(null);
  const lastSnapRef = useRef<number | null>(null);

  // Move the playhead and progress before the seek, so they never wait on decoding.
  const showPosition = useCallback((time: number) => {
    playheadRef.current?.updatePosition(time);
    videoTrackLayerRef.current?.updateProgress(time);
  }, []);

  const handleSeek = useCallback(
    (time: number) => {
      showPosition(time);
      seek(time);
    },
    [seek, showPosition],
  );

  const handleScrub = useCallback(
    (rawTime: number | null) => {
      if (rawTime === null) {
        scrubSnapPointsRef.current = null;
        lastSnapRef.current = null;
        scrubSeek?.(null);
        setIsScrubbing(false);
        return;
      }
      // Tracking may publish new fragments during a drag. Keep that gesture's
      // targets stable; the next gesture picks up the new visible boundaries.
      const gestureSnapPoints = (scrubSnapPointsRef.current ??= snapPoints);
      setIsScrubbing(true);
      // Scrubbing ends a hover preview, but keeps the pinned track focused.
      setHighlightedSegmentIds([]);

      const snapThresholdInSeconds = SNAP_THRESHOLD_PX / state.pixelsPerSecond;
      let bestSnap: number | null = null;
      let minDistance = snapThresholdInSeconds;

      for (const point of gestureSnapPoints) {
        const distance = Math.abs(point - rawTime);
        if (distance < minDistance) {
          minDistance = distance;
          bestSnap = point;
        }
      }

      if (bestSnap !== null && bestSnap !== lastSnapRef.current) playheadRef.current?.pulse();
      lastSnapRef.current = bestSnap;
      const time = bestSnap ?? rawTime;
      showPosition(time);
      (scrubSeek ?? seek)(time);
    },
    [scrubSeek, seek, setIsScrubbing, setHighlightedSegmentIds, showPosition, state.pixelsPerSecond, snapPoints],
  );

  // Keyboard zoom keeps the playhead in place while it's in view.
  const zoomViewport = state.zoom;
  const zoomAroundPlayhead = useCallback(
    (zoom: TimelineZoom) => {
      const x = bridge.getCurrentTime() * state.pixelsPerSecond - state.scrollLeft;
      zoomViewport(zoom, x >= 0 && x <= width ? x : width / 2);
    },
    [bridge, state.pixelsPerSecond, state.scrollLeft, zoomViewport, width],
  );
  useLayoutEffect(() => {
    if (!zoomRef) return;
    zoomRef.current = zoomAroundPlayhead;
    return () => {
      if (zoomRef.current === zoomAroundPlayhead) zoomRef.current = null;
    };
  }, [zoomRef, zoomAroundPlayhead]);

  const handleRulerKeyDown = (event: React.KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const now = bridge.getCurrentTime();
    const page = durationSeconds / 10;
    const steps: Partial<Record<string, number>> = {
      ArrowLeft: -SEEK_STEP_SECONDS,
      ArrowDown: -SEEK_STEP_SECONDS,
      ArrowRight: SEEK_STEP_SECONDS,
      ArrowUp: SEEK_STEP_SECONDS,
      PageDown: -page,
      PageUp: page,
    };
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const boundary = event.key === "Home" ? "first" : "last";
      if (bridge.seekBoundaryFrame) bridge.seekBoundaryFrame(boundary);
      else handleSeek(boundary === "first" ? 0 : durationSeconds);
      return;
    }
    const step = steps[event.key];
    if (step === undefined) return;
    event.preventDefault();
    handleSeek(clamp(now + step, 0, durationSeconds));
  };

  const { collapseFr } = useSpring({
    collapseFr: isMinimized ? 0 : 1,
    config: ANIM_CONFIG,
  });

  const bottomHeight = to([collapseFr, state.animatedTotalHeight], (c: number, h: number) => c * h);

  const gridColsClass = isMinimized
    ? "grid-cols-[0px_minmax(0,1fr)]"
    : "grid-cols-[100px_minmax(0,1fr)] md:grid-cols-[200px_minmax(0,1fr)]";

  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-col transition-[padding] duration-150 ease-out",
        isMinimized ? "pb-2" : "pb-0",
      )}
      ref={containerRef}
      style={{
        touchAction: "none",
        overflowX: "visible",
        overflowY: "clip",
      }}
      onPointerLeave={() => hoverRef.current?.setPointerX(null)}
      onPointerMove={(event) => {
        const rect = rulerContainerRef.current?.getBoundingClientRect();
        const overOverview = (event.target as Element).closest?.("[data-timeline-overview]");
        hoverRef.current?.setPointerX(
          event.pointerType === "mouse" && rect && !overOverview ? event.clientX - rect.left : null,
        );
      }}
    >
      <div className={cn("grid w-full transition-[grid-template-columns] duration-150 ease-out", gridColsClass)}>
        <div className="col-span-1 overflow-hidden" style={{ height: RULER_HEIGHT }} />
        <div
          aria-label={strings.videoPlayer.timelinePosition}
          aria-valuemax={Math.floor(durationSeconds)}
          aria-valuemin={0}
          className="relative col-span-1 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-blue"
          // Player shortcuts other than this slider's own keys stay live while it has focus.
          data-timeline-ruler
          ref={rulerContainerRef}
          role="slider"
          style={{ height: RULER_HEIGHT }}
          tabIndex={0}
          onKeyDown={handleRulerKeyDown}
        >
          <RulerCanvas
            durationSeconds={durationSeconds}
            height={RULER_HEIGHT}
            maxScroll={state.maxScroll}
            pixelsPerSecond={state.pixelsPerSecond}
            scrollLeft={state.scrollLeft}
            setScrollLeft={state.setScrollLeft}
            width={width}
            onScrub={handleScrub}
          />
          <TimelineOverview
            bridge={bridge}
            maxScroll={state.maxScroll}
            pixelsPerSecond={state.pixelsPerSecond}
            scrollLeft={state.scrollLeft}
            setScrollLeft={state.setScrollLeft}
            viewportWidth={width}
          />
        </div>

        <div
          className={cn(
            "col-span-1 flex max-w-full items-center overflow-hidden pb-2 transition-opacity duration-150 ease-out",
            isMinimized ? "opacity-0" : "opacity-100",
          )}
          style={{ height: SCRUB_BAR_ROW_HEIGHT }}
        >
          <TimeDisplayContainer bridge={bridge} duration={durationSeconds} />
        </div>

        <div className="relative col-span-1" ref={videoTrackContainerRef} style={{ height: SCRUB_BAR_ROW_HEIGHT }}>
          {videoTrack && (
            <ScrubBarCanvas
              durationSeconds={durationSeconds}
              height={ROW_HEIGHT_TRACK}
              maxScroll={state.maxScroll}
              pixelsPerSecond={state.pixelsPerSecond}
              scrollLeft={state.scrollLeft}
              setScrollLeft={state.setScrollLeft}
              videoTrackRef={videoTrackLayerRef}
              width={width}
              onScrub={handleScrub}
            />
          )}
          <HoverIndicator
            bottomHeight={bottomHeight}
            isMinimized={isMinimized}
            numOfRenderableRows={state.renderableRows.length}
            pixelsPerSecond={state.pixelsPerSecond}
            ref={hoverRef}
            scrollLeft={state.scrollLeft}
            scrubBarHeight={SCRUB_BAR_ROW_HEIGHT}
            viewportWidth={width}
          />
          <Playhead
            bottomHeight={bottomHeight}
            bridge={bridge}
            isMinimized={isMinimized}
            numOfRenderableRows={state.renderableRows.length}
            pixelsPerSecond={state.pixelsPerSecond}
            ref={playheadRef}
            scrollLeft={state.scrollLeft}
            scrubBarHeight={SCRUB_BAR_ROW_HEIGHT}
            viewportWidth={width}
          />
        </div>
      </div>

      <animated.div
        className={cn(
          "grid min-h-0 w-full overflow-hidden transition-[grid-template-columns,opacity] duration-150 ease-out",
          gridColsClass,
          isMinimized ? "opacity-0" : "opacity-100",
        )}
        style={{
          flexGrow: 0,
          flexShrink: 1,
          flexBasis: bottomHeight.to((h: number) => `${h}px`),
          minHeight: 0,
        }}
      >
        <div
          className="timeline-label-pane col-span-1 min-h-0 overflow-y-auto overflow-x-hidden"
          ref={labelPaneRef}
          onScroll={handleLabelScroll}
        >
          <TrackLabelPane
            animatedTotalHeight={state.animatedTotalHeight}
            transitions={state.transitions}
            onCollapseToggle={state.handleCollapseToggle}
          />
        </div>

        <div className="relative col-span-1 min-h-0 overflow-hidden" ref={combinedTimelineRef}>
          <TracksCanvas
            durationSeconds={durationSeconds}
            focus={focus}
            height={height}
            maxScroll={state.maxScroll}
            pixelsPerSecond={state.pixelsPerSecond}
            ref={tracksCanvasRef}
            scrollLeft={state.scrollLeft}
            scrollTop={state.scrollTop}
            setScrollLeft={state.setScrollLeft}
            transitions={state.transitions}
            width={width}
            onScrub={handleScrub}
            onSegmentClick={handleSeek}
          />
          <SummaryOverlaySvg
            durationSeconds={durationSeconds}
            height={height}
            pixelsPerSecond={state.pixelsPerSecond}
            rows={state.renderableRows}
            scrollLeft={state.scrollLeft}
            scrollTop={state.scrollTop}
            trackingTimeline={trackingTimeline}
            width={width}
          />
        </div>
      </animated.div>
    </div>
  );
});

TimelineEditor.displayName = "TimelineEditor";
